import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { useTranslation } from "react-i18next";
import { useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, MapPin } from "lucide-react";
import { IndonesiaMapBackground } from "./IndonesiaMapBackground";
import "swiper/css";
import "swiper/css/pagination";

export function SlidePimpinanLPTK() {
    const { t } = useTranslation('public');
    const swiperRef = useRef<SwiperType | null>(null);

    const pimpinanlptk = useMemo(() => [
        {
            name: "Prof. Dr. I Wayan Lasmawan, M.Pd.",
            university: "Universitas Pendidikan Ganesha",
            position: t('leaders.positions.rector'),
            location: "Singaraja, Bali",
            image: "/rektor/rektor_undiksha.jpg",
            logo: "/logo/logo_undiksha.png",
            website: "https://undiksha.ac.id"
        },
        {
            name: "Prof. Dr. Nurhasan, M.Kes.",
            university: "Universitas Negeri Surabaya",
            position: t('leaders.positions.rector'),
            location: "Surabaya, Jawa Timur",
            image: "/rektor/rektor_unesa.jpg",
            logo: "/logo/logo_unesa.png",
            website: "https://unesa.ac.id"
        },
        {
            name: "Prof. Dr. Didi Sukyadi, M.A.",
            university: "Universitas Pendidikan Indonesia",
            position: t('leaders.positions.rector'),
            location: "Bandung, Jawa Barat",
            image: "/rektor/rektor_upi.jpg",
            logo: "/logo/logo_upi.png",
            website: "https://www.upi.edu"
        },
        {
            name: "Prof. Dr. Komarudin, M.Si.",
            university: "Universitas Negeri Jakarta",
            position: t('leaders.positions.rector'),
            location: "Jakarta Timur, DKI Jakarta",
            image: "/rektor/rektor_unj.jpg",
            logo: "/logo/logo_unj.png",
            website: "https://www.unj.ac.id"
        },
        {
            name: "Prof. Dr. Sumaryanto, M.Kes., AIFO.",
            university: "Universitas Negeri Yogyakarta",
            position: t('leaders.positions.rector'),
            location: "Sleman, D.I. Yogyakarta",
            image: "/rektor/rektor_uny.jpg",
            logo: "/logo/logo_uny.png",
            website: "https://www.uny.ac.id"
        },
        {
            name: "Prof. Dr. S. Martono, M.Si.",
            university: "Universitas Negeri Semarang",
            position: t('leaders.positions.rector'),
            location: "Semarang, Jawa Tengah",
            image: "/rektor/rektor_unnes.jpg",
            logo: "/logo/logo_unnes.png",
            website: "https://unnes.ac.id"
        },
        {
            name: "Prof. Dr. Hariyono, M.Pd.",
            university: "Universitas Negeri Malang",
            position: t('leaders.positions.rector'),
            location: "Malang, Jawa Timur",
            image: "/rektor/rektor_um.jpg",
            logo: "/logo/logo_um.png",
            website: "https://um.ac.id"
        },
        {
            name: "Prof. Dr. Baharuddin, S.T., M.Pd.",
            university: "Universitas Negeri Medan",
            position: t('leaders.positions.rector'),
            location: "Medan, Sumatera Utara",
            image: "/rektor/rektor_unimed.jpg",
            logo: "/logo/logo_unimed.png",
            website: "https://www.unimed.ac.id"
        },
        {
            name: "Dr. Ir. Krismadinata, S.T., M.T.",
            university: "Universitas Negeri Padang",
            position: t('leaders.positions.rector'),
            location: "Padang, Sumatera Barat",
            image: "/rektor/rektor_unp.jpg",
            logo: "/logo/logo_unp.png",
            website: "https://www.unp.ac.id"
        },
        {
            name: "Prof. Dr. Farida Patittingi, S.H., M.Hum.",
            university: "Universitas Negeri Makassar",
            position: t('leaders.positions.acting_rector'),
            location: "Makassar, Sulawesi Selatan",
            image: "/rektor/rektor_plh_unm.jpg",
            logo: "/logo/logo_unm.png",
            website: "https://unm.ac.id"
        },
        {
            name: "Dr. Joseph Philip Kambey, S.E., Ak., M.B.A.",
            university: "Universitas Negeri Manado",
            position: t('leaders.positions.rector'),
            location: "Tondano, Sulawesi Utara",
            image: "/rektor/rektor_unima.jpg",
            logo: "/logo/logo_unima.png",
            website: "https://unima.ac.id"
        },
        {
            name: "Prof. Dr. Ir. Eduart Wolok, S.T., M.T.",
            university: "Universitas Negeri Gorontalo",
            position: t('leaders.positions.rector'),
            location: "Gorontalo, Gorontalo",
            image: "/rektor/rektor_ung.jpg",
            logo: "/logo/logo_ung.png",
            website: "https://www.ung.ac.id"
        },
    ], [t]);

    return (
        <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 dark:from-slate-900 dark:via-blue-950/10 dark:to-indigo-950/20 relative overflow-hidden">
            {/* Background Peta Indonesia - dekoratif & subtle, lihat IndonesiaMapBackground.tsx */}
            <IndonesiaMapBackground variant="vector" className="opacity-90 dark:opacity-75" />

            <div className="w-full max-w-[94%] xl:max-w-[92%] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div>
                    {/* Header */}
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-primary/10">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                            {t('leaders.badge', 'Pimpinan Forum LPTK Indonesia')}
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-3 leading-tight">
                            {t('leaders.title')}
                        </h2>
                        <h2 className="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                            {t('leaders.subtitle')}
                        </h2>
                    </div>

                    {/* Navigasi Panah Kiri & Kanan */}
                    <div className="flex items-center justify-end gap-3 mb-6 px-2">
                        <button
                            onClick={() => swiperRef.current?.slidePrev()}
                            className="w-11 h-11 rounded-full bg-white dark:bg-gray-900 hover:bg-primary border border-slate-200 dark:border-slate-800 hover:border-primary shadow-sm hover:shadow-lg hover:shadow-primary/20 flex items-center justify-center text-slate-700 dark:text-slate-300 hover:text-white transition-all duration-300 group/btn"
                            aria-label="Previous slide"
                        >
                            <ChevronLeft className="w-5 h-5 transition-transform group-hover/btn:-translate-x-0.5" />
                        </button>
                        <button
                            onClick={() => swiperRef.current?.slideNext()}
                            className="w-11 h-11 rounded-full bg-white dark:bg-gray-900 hover:bg-primary border border-slate-200 dark:border-slate-800 hover:border-primary shadow-sm hover:shadow-lg hover:shadow-primary/20 flex items-center justify-center text-slate-700 dark:text-slate-300 hover:text-white transition-all duration-300 group/btn"
                            aria-label="Next slide"
                        >
                            <ChevronRight className="w-5 h-5 transition-transform group-hover/btn:translate-x-0.5" />
                        </button>
                    </div>

                    {/* Swiper Slider */}
                    <Swiper
                        modules={[Autoplay, Pagination]}
                        onBeforeInit={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        spaceBetween={28}
                        loop={true}
                        autoplay={{
                            delay: 3500,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                        }}
                        pagination={{
                            clickable: true,
                            dynamicBullets: true,
                        }}
                        breakpoints={{
                            0: { slidesPerView: 1.15, spaceBetween: 16 },
                            640: { slidesPerView: 2.15, spaceBetween: 24 },
                            1024: { slidesPerView: 3, spaceBetween: 28 },
                            1280: { slidesPerView: 4, spaceBetween: 32 },
                        }}
                        className="pb-16 pt-3 !px-3 -mx-3 [&_.swiper-pagination-bullet]:bg-slate-300 dark:[&_.swiper-pagination-bullet]:bg-slate-700 [&_.swiper-pagination-bullet-active]:!bg-primary"
                    >
                        {pimpinanlptk.map((leader, index) => (
                            <SwiperSlide key={index} className="h-auto pb-6">
                                {/* Kartu dengan avatar bulat elegan - foto di-zoom penuh mengisi lingkaran
                                    (object-cover, crop dari tengah), dibungkus ring & shadow lembut. */}
                                <div className="group relative bg-white dark:bg-gray-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col h-full min-h-[380px] overflow-hidden">

                                    {/* Tombol Kunjungi Website - z-20 (lebih tinggi dari wrapper konten di bawahnya
                                        yang sama-sama z-10, supaya tombol ini yang menangkap klik, bukan tertutup) */}
                                    <a
                                        href={leader.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute top-3.5 right-3.5 z-20 w-8 h-8 rounded-full bg-slate-50 hover:bg-primary dark:bg-slate-800 text-slate-500 hover:text-white dark:text-slate-400 border border-slate-100 dark:border-slate-700 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                                        title={`Kunjungi Website Resmi ${leader.university}`}
                                        aria-label={`Website ${leader.university}`}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>

                                    {/* Ornamen Ukiran Bali - aksen pojok kiri & kanan bawah kartu */}
                                    <img
                                        src="/ornamen/ukiran-corner-bottom.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute bottom-0 left-0 w-12 sm:w-14 opacity-[0.15] dark:opacity-[0.12] pointer-events-none select-none z-0"
                                    />
                                    <img
                                        src="/ornamen/ukiran-corner-bottom.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute bottom-0 right-0 w-12 sm:w-14 scale-x-[-1] opacity-[0.15] dark:opacity-[0.12] pointer-events-none select-none z-0"
                                    />

                                    <div className="relative z-10 w-full flex flex-col items-center px-5 pt-8">
                                        {/* Avatar Bulat - foto di-zoom penuh mengisi lingkaran (object-cover),
                                            crop dari tengah supaya wajah tetap jadi fokus utama */}
                                        <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full p-[3px] bg-gradient-to-br from-primary/40 via-blue-300/40 to-amber-300/40 shadow-md mb-3.5 flex-shrink-0 group-hover:from-primary group-hover:via-blue-400 group-hover:to-amber-400 transition-colors duration-300">
                                            <div className="w-full h-full rounded-full overflow-hidden bg-slate-50 dark:bg-slate-800 ring-4 ring-white dark:ring-gray-900">
                                                <img
                                                    src={leader.image}
                                                    alt={leader.name}
                                                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                                />
                                            </div>
                                        </div>

                                        {/* Nama */}
                                        <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-2 leading-snug line-clamp-2 min-h-[36px] flex items-center justify-center text-center group-hover:text-primary dark:group-hover:text-light transition-colors">
                                            {leader.name}
                                        </h4>

                                        {/* Jabatan */}
                                        <span className="inline-flex items-center bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 font-bold text-[10px] tracking-wide px-2.5 py-0.5 rounded-full border border-blue-200/70 dark:border-blue-500/20 mb-3">
                                            {leader.position}
                                        </span>
                                    </div>

                                    {/* Logo & Nama Universitas + Lokasi */}
                                    <div className="relative z-10 w-full px-5 pt-4 pb-6 mt-auto border-t border-slate-100 dark:border-slate-800/80 flex flex-col items-center gap-2 text-slate-700 dark:text-slate-300">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center p-1 flex-shrink-0">
                                                <img
                                                    src={leader.logo}
                                                    alt={leader.university}
                                                    className="w-full h-full object-contain"
                                                    onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                                                />
                                            </div>
                                            <p className="text-xs sm:text-sm font-semibold line-clamp-1">
                                                {leader.university}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 font-medium">
                                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                            <span className="line-clamp-1">{leader.location}</span>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </section>
    );
}